import React from 'react';
import { NavLink } from 'react-router-dom';
import Drawer              from '@mui/material/Drawer';
import Box                 from '@mui/material/Box';
import Typography          from '@mui/material/Typography';

import DashboardRoundedIcon      from '@mui/icons-material/DashboardRounded';
import CategoryRoundedIcon       from '@mui/icons-material/CategoryRounded';
import AttachMoneyRoundedIcon    from '@mui/icons-material/AttachMoneyRounded';
import UploadFileRoundedIcon     from '@mui/icons-material/UploadFileRounded';
import TuneRoundedIcon           from '@mui/icons-material/TuneRounded';
import CalculateRoundedIcon      from '@mui/icons-material/CalculateRounded';
import HistoryRoundedIcon        from '@mui/icons-material/HistoryRounded';
import ConstructionRoundedIcon   from '@mui/icons-material/ConstructionRounded';
import PeopleRoundedIcon         from '@mui/icons-material/PeopleRounded';
import GroupsRoundedIcon         from '@mui/icons-material/GroupsRounded';
import SecurityRoundedIcon       from '@mui/icons-material/SecurityRounded';
import HandymanRoundedIcon       from '@mui/icons-material/HandymanRounded';
import LinkRoundedIcon           from '@mui/icons-material/LinkRounded';
import BuildCircleRoundedIcon    from '@mui/icons-material/BuildCircleRounded';
import ChevronRightIcon          from '@mui/icons-material/ChevronRight';

import { useAuth } from '../../context/AuthContext';

export const DRAWER_WIDTH = 260;

/* ── Nav item definitions ─────────────────────────────────────────────────────
 * color: icon colour in the inactive state.
 * On the active (blue) row the icon is overridden to white via CSS.
 * ─────────────────────────────────────────────────────────────────────────── */
const MAIN_ITEMS = [
  { label: 'Dashboard',            Icon: DashboardRoundedIcon,    path: '/',                     color: '#A78BFA' },
  { label: 'Master Data',          Icon: CategoryRoundedIcon,     path: '/master-data',          color: '#60A5FA' },
  { label: 'Price Management',     Icon: AttachMoneyRoundedIcon,  path: '/prices',               color: '#34D399' },
  { label: 'Material Excel',       Icon: UploadFileRoundedIcon,   path: '/excel',                color: '#22D3EE' },
  { label: 'Header Config',        Icon: TuneRoundedIcon,         path: '/header-config',        color: '#06B6D4' },
  { label: 'Accessory Prices',     Icon: HandymanRoundedIcon,     path: '/accessory-prices',     color: '#FFA726' },
  { label: 'Material-Accessories', Icon: LinkRoundedIcon,         path: '/material-accessories', color: '#FF7043' },
  { label: 'Accessory Excel',      Icon: BuildCircleRoundedIcon,  path: '/accessory-excel',      color: '#4ADE80' },
  { label: 'New Estimation',       Icon: CalculateRoundedIcon,    path: '/estimation',           color: '#818CF8' },
  { label: 'Estimation History',   Icon: HistoryRoundedIcon,      path: '/estimation-history',   color: '#38BDF8' },
];

const ADMIN_ITEMS = [
  { label: 'Users',                Icon: PeopleRoundedIcon,   path: '/admin/users',      color: '#93C5FD' },
  { label: 'Groups & Permissions', Icon: GroupsRoundedIcon,   path: '/admin/groups',     color: '#C4B5FD' },
  { label: 'Audit Logs',           Icon: SecurityRoundedIcon, path: '/admin/audit-logs', color: '#FCA5A5' },
];

/* ── CSS — module-level constant so React never diffs it ─────────────────── */
const SIDEBAR_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  .epc-sb, .epc-sb * {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
    box-sizing: border-box;
  }

  /* ── Base item ──────────────────────────────────────────────────────────── */
  .sb-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    margin: 1px 8px;
    border-radius: 10px;
    text-decoration: none;
    color: rgba(255, 255, 255, 0.78);
    font-size: 13.5px;
    font-weight: 500;
    letter-spacing: 0.1px;
    transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
    outline: none;
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    white-space: nowrap;
    overflow: hidden;
  }

  /* ── Hover ──────────────────────────────────────────────────────────────── */
  .sb-item:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.07);
    transform: translateX(2px);
  }

  /* ── Active (current page) ──────────────────────────────────────────────── */
  .sb-item.sb-active {
    color: #ffffff;
    font-weight: 600;
    background: linear-gradient(135deg, #1565C0 0%, #1E88E5 100%);
    box-shadow: 0 4px 16px rgba(21, 101, 192, 0.40);
  }
  .sb-item.sb-active:hover {
    transform: none;
    background: linear-gradient(135deg, #1565C0 0%, #2196F3 100%);
  }

  /* Icon turns white on active row, overriding the MUI colour class */
  .sb-item.sb-active .sb-icon svg {
    color: #ffffff !important;
  }

  /* ── Icon wrapper ────────────────────────────────────────────────────────── */
  .sb-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    transition: transform 0.18s ease;
  }
  .sb-item:hover .sb-icon {
    transform: scale(1.12);
  }

  /* ── Label ───────────────────────────────────────────────────────────────── */
  .sb-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Section heading ────────────────────────────────────────────────────── */
  .sb-heading {
    padding: 16px 20px 5px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 2px;
    color: rgba(255, 255, 255, 0.28);
    text-transform: uppercase;
  }

  /* ── Scrollable container ────────────────────────────────────────────────── */
  .sb-scroll { overflow-y: auto; overflow-x: hidden; }
  .sb-scroll::-webkit-scrollbar { width: 3px; }
  .sb-scroll::-webkit-scrollbar-track { background: transparent; }
  .sb-scroll::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.10);
    border-radius: 2px;
  }
  .sb-scroll::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.20);
  }
`;

/* ── Single nav item ─────────────────────────────────────────────────────── */
const NavItem = React.memo(function NavItem({ label, Icon, path, color }) {
  return (
    <NavLink
      to={path}
      end={path === '/'}
      className={({ isActive }) => `sb-item${isActive ? ' sb-active' : ''}`}
    >
      <span className="sb-icon">
        <Icon sx={{ fontSize: 20, color, display: 'block' }} />
      </span>
      <span className="sb-label">{label}</span>
    </NavLink>
  );
});

/* ── Thin divider ────────────────────────────────────────────────────────── */
const Divider = () => (
  <Box sx={{ mx: 2.5, my: '10px', height: '1px', bgcolor: 'rgba(255,255,255,0.07)' }} />
);

/* ─────────────────────────────────────────────────────────────────────────── */

// Shared drawer paper styling — identical for the mobile (temporary) and
// desktop (permanent) variants below, so they're pixel-for-pixel the same
// sidebar, just mounted differently per viewport.
const paperSx = {
  width: DRAWER_WIDTH,
  height: '100vh',
  boxSizing: 'border-box',
  background: 'linear-gradient(180deg, #0D1B3E 0%, #0A1628 100%)',
  borderRight: '1px solid rgba(255,255,255,0.05)',
  boxShadow: '4px 0 24px rgba(0,0,0,0.45)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const Sidebar = ({ mobileOpen = false, onClose = () => {} }) => {
  const { isAdmin, hasPermission } = useAuth();
  const showAudit        = isAdmin || hasPermission('VIEW_AUDIT_LOGS');
  const showDashboard    = isAdmin || hasPermission('VIEW_DASHBOARD');
  const showHeaderConfig = isAdmin || hasPermission('MANAGE_HEADER_CONFIG');
  const showUsers        = isAdmin || hasPermission('MANAGE_USERS');
  const showGroups       = isAdmin || hasPermission('MANAGE_PERMISSIONS');

  const content = (
    <Box className="epc-sb" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

      {/* ── Logo ────────────────────────────────────────────────────── */}
      <Box sx={{
        px: 2.5, pt: 2.5, pb: 2.2,
        display: 'flex', alignItems: 'center', gap: 1.5,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        flexShrink: 0,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: '11px',
          background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: '0 3px 12px rgba(249,115,22,0.45)',
        }}>
          <ConstructionRoundedIcon sx={{ fontSize: 22, color: '#fff' }} />
        </Box>
        <Box>
          <Typography sx={{
            fontSize: 17, fontWeight: 800, color: '#fff',
            lineHeight: 1.15, letterSpacing: 0.5,
          }}>
            Material
          </Typography>
          <Typography sx={{
            fontSize: 9, fontWeight: 600,
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: 1.8, textTransform: 'uppercase',
          }}>
            Master
          </Typography>
        </Box>
      </Box>

      {/* ── Scrollable nav ───────────────────────────────────────────── */}
      <Box className="sb-scroll" sx={{ flex: 1, pt: 0.5, pb: 1 }}>

        <div className="sb-heading">Main Menu</div>

        {MAIN_ITEMS.filter((item) => {
          if (item.path === '/')              return showDashboard;
          if (item.path === '/header-config') return showHeaderConfig;
          return true;
        }).map((item) => (
          <NavItem key={item.path} {...item} />
        ))}

        {(showUsers || showGroups || showAudit) && (
          <>
            <Divider />

            {/* Admin section label with arrow hint */}
            <Box sx={{
              px: '20px', pb: '5px', pt: '4px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Typography sx={{
                fontSize: 10, fontWeight: 700, letterSpacing: 2,
                color: 'rgba(255,255,255,0.28)', textTransform: 'uppercase',
              }}>
                Administration
              </Typography>
              <ChevronRightIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.20)' }} />
            </Box>

            {ADMIN_ITEMS.filter((item) => {
              if (item.path === '/admin/audit-logs') return showAudit;
              if (item.path === '/admin/groups') return showGroups;
              return showUsers;
            }).map((item) => (
              <NavItem key={item.path} {...item} />
            ))}
          </>
        )}
      </Box>

    </Box>
  );

  return (
    <>
      <style>{SIDEBAR_CSS}</style>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {/* Mobile — off-canvas overlay (position: fixed under the hood, via
            MUI's Modal), toggled by TopBar's hamburger. Never affects the
            main content's layout width since it isn't in flex flow. */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': paperSx,
          }}
        >
          {content}
        </Drawer>

        {/* Desktop — permanent, in-flow column, exactly as before. */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': paperSx,
          }}
          open
        >
          {content}
        </Drawer>
      </Box>
    </>
  );
};

export default Sidebar;
